/**
 * Copyright 2016 xdot GmbH
 *
 * Author: Carsten Hibbeler
 * Date: 08.07.2016
 *
 */

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { equal, equalArray, shallowEqual } from '../utils/shallowEqual';
import { amdDefine, amdRequire } from '../utils/amd-utils';


const optionEq = (a, b) => {
    return Array.isArray(a) ? equalArray(a, b) : shallowEqual(a, b);
};


const eq = (a, b, k) => {
    return k === 'options' ? equal(a, b, optionEq) : a === b;
};

class PluginComponent extends React.Component {

    constructor(props, plugins, dependencies) {
        super(props);
        this.state = {
            ...this.loadPlugins(plugins || []),
            ...this.loadDependencies(dependencies || [])
        };
    }

    componentDidMount() {
        this.componentDidUpdate();
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!shallowEqual(this.props, nextProps)) {
            return true;
        }
        return !equal(this.state, nextState, eq);
    }

    /**
     * Props maybe changed by the plugins and than set as options in state.
     * Plugins should not use the options for updates!
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.init) {
            if (!equalArray(this.props.plugins, nextProps.plugins)) {
                console.warn('Plugins changed', this.props.plugins, nextProps.plugins);
                this.onPluginsChanged(nextProps.plugins);
            } else {
                const options = this.plugins().filter(p => p.options)
                    .reduce((o, p) => p.options(o) || o, { ...nextProps });
                this._setOptions(options);
            }
        }
    }

    /**
     * After render react to state changes (init or update)
     **/
    componentDidUpdate(prevProps, prevState) {
        let o = this.state.options;
        if (__DEV__) {
            o = o && Object.freeze(o);
        }
        if (this.arePluginsLoaded() && this.areDependeciesLoaded()) {
            if (!this.init) {
                //Initialisation + initOptions phase
                this._initWithPlugins();
            } else {
                //Update phase
                this.updateWithPlugins(o);
            }

        }
    }

    /**
     *  Destroy map and remove from plugin
     */
    componentWillUnmount() {
        this.plugins().filter(p => p.remove).forEach(p => p.remove());
    }

    onPluginsChanged(newPlugins) {
        this.plugins().filter(p => p.remove).forEach(p => p.remove());
        this.setState({ ...this.loadPlugins(newPlugins || []) });
        this.init = false;
        this.reinit = true;
    }

    _initWithPlugins() {
        this.init = true;
        const options = this.plugins().filter(p => {
            if (p.initOptions) {
                return true;
            }
            if (p.options) {
                console.warn('Plugin overrides options but not initOptions', p);
            }
        }).reduce((o, p) => p.initOptions(o) || o, { ...this.props });
        if (this.reinit) {
            this.reinitWithPlugins(options);
            this.reinit = false;
        } else {
            this.initWithPlugins(options);
        }
        this._setOptions(options); //set first or reinit options (will trigger render)
    }

    _setOptions(options) {
        //console.info('PluginComponent => update options '/*, { before: this.state.options, after: options }*/);
        this.setState({ options });
    }


    /**
     * First component initialization.
     *
     * Override and:
     * 1. init this.mycomponent
     * 2. call this.callPluginsInit(this.mycomponent, o);
     *
     * @param o
     */
    initWithPlugins(o) {
        console.warn('Implement initWithPlugins(o)', o);
    }

    /**
     * Component reinitialization.
     *
     * Override and:
     * 1. optional reinit this.mycomponent
     * 2. call this.callPluginsInit(this.mycomponent, o);
     *
     * @param o
     */
    reinitWithPlugins(o) {
        console.warn('Implement reinitWithPlugins(o)', o);
    }

    /**
     * Component update.
     *
     * Override and:
     * 1. update this.mycomponent
     * 2. call this.callPluginsUpdate(this.mycomponent, o);
     *
     * @param o
     */
    updateWithPlugins(o) {
        console.warn('Implement updateWithPlugins(o)', o);
    }

    callPluginsInit(...args) {
        const reEvaluate = this.reEvaluate.bind(this);
        //console.info('PluginComponent => plugin.init()', args);
        this.plugins().filter(p => p.init).forEach(p => p.init.apply(p, args.concat(reEvaluate)));
    }

    callPluginsUpdate(...args) {
        //console.info('PluginComponent => plugin.update()', args);
        this.plugins().filter(p => p.update).forEach(p => p.update.apply(p, args));
    }

    arePluginsLoaded() {
        return !this.state.loadPlugins;
    }

    areDependeciesLoaded() {
        return !this.state.loadDependencies;
    }

    plugins() {
        return this.state.plugins || [];
    }


    reEvaluate(additionalProps) {
        //to check if mounted
        this.UNSAFE_componentWillReceiveProps({
            ...this.props,
            ...additionalProps
        });
    }

    loadDependencies(deps) {
        const dependencies = {};

        if (!deps || deps.length === 0) {
            return { loadDependencies: false };
        }

        deps.forEach(depName => {
            amdRequire([ depName ], dep => {
                dependencies[ depName ] = dep;
                const loadedDeps = Object.keys(dependencies).length;
                this.setState({
                    dependencies,
                    loadDependencies: loadedDeps !== deps.length
                });
            });
        });
        return { loadDependencies: true };
    }

    loadPlugins(plugins) {
        const filterAndLogNull = p => {
            if (!p) {
                console.error('Found \'null\' value in plugin props!', this);
            }
            return !!p;
        };
        const async = plugins.map(this.asAsyncPlugin).filter(p => !!p).map(p => p.path.trim());
        if (async.length > 0) {
            amdRequire(async, () => {
                plugins = plugins.map(p => {
                    try {
                        const { path, config } = this.asAsyncPlugin(p) || {};
                        if (path) {
                            try {
                                p = amdRequire(path);
                            } catch (e) {
                                console.error(e);
                                return null;
                            }
                        }
                        return this.asPlugin(p, config);
                    } catch (e) {
                        console.error('Error loading Plugin ' + p, e);
                    }
                }).filter(filterAndLogNull);
                this.setState({ plugins, loadPlugins: false });
            }, function (err) {
                //The error has a list of modules that failed
                let pluginErrorIds = err.requireModules && err.requireModules;
                console.error('Error loading plugins', pluginErrorIds);
                const failPlugin = {};
                pluginErrorIds.forEach(plugin => {
                    //undef is function only on the global requirejs object.
                    //Use it to clear internal knowledge of the module. Any modules
                    //that were dependent on the module and in the middle of loading
                    //will not be loaded yet, they will wait until a valid the module
                    //does load.
                    amdRequire.undef(plugin);
                    amdDefine(plugin, [], () => failPlugin);
                    amdRequire([ plugin ], function () {
                    });
                });
            });
            return { loadPlugins: true };
        } else {
            return { plugins: plugins.map(this.asPlugin.bind(this)).filter(filterAndLogNull), loadPlugins: false };
        }
    }

    asPlugin(p, config) {
        const plugin = typeof p === 'function' ? new p(this.props, config) : Object.create(p);
        plugin.context = this.context;
        return plugin;
    }

    asAsyncPlugin(p) {
        if (typeof p === 'string') {
            return { path: p };
        } else if (p && typeof p.path === 'string') {
            return {
                path: p.path,
                config: typeof p.config === 'string' ?
                    (p.config.trim().length > 0 ? JSON.parse(p.config) : null) : p
            };
        }
        else if (typeof p === 'function') {
            //Do nothing plugin allready loaded
        }
        else {
            console.error('Error loading plugin as async. Plugin is not valid or undefined', p);
        }
    }
}


PluginComponent.propTypes = {
    plugins: PropTypes.arrayOf(
        PropTypes.oneOfType([ PropTypes.func, PropTypes.object, PropTypes.string ])
    )
};

export default PluginComponent;




// WEBPACK FOOTER //
// ./js/components/PluginComponent.js